import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import maplibregl, { Map as MLMap, Popup } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api, FacilitiesGeoJSON } from "../lib/api";

const STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

const STATUS_COLORS: Record<string, string> = {
  active: "#48bb78",
  new: "#4299e1",
  war_zone: "#e53e3e",
  relocated: "#ed8936",
  closed: "#a0aec0",
};

// Палітра для score-градієнта (червоний → жовтий → зелений)
const SCORE_GRADIENT = [
  { stop: 0, color: "#c53030" },     // 0 — критично
  { stop: 25, color: "#dd6b20" },    // 25 — слабо
  { stop: 50, color: "#d69e2e" },    // 50 — середньо
  { stop: 75, color: "#48bb78" },    // 75 — добре
  { stop: 100, color: "#22543d" },   // 100 — топ
];

type ColorMode = "status" | "composite" | "activity" | "efficiency"
                | "care_routing" | "financial_health" | "dynamics";
type ViewMode = "facilities" | "network_only" | "all_points";

const COLOR_MODES: { value: ColorMode; label: string; isScore: boolean }[] = [
  { value: "status", label: "Статус", isScore: false },
  { value: "composite", label: "Composite (зведений бал)", isScore: true },
  { value: "activity", label: "Активність", isScore: true },
  { value: "efficiency", label: "Ефективність", isScore: true },
  { value: "care_routing", label: "Маршрутизація", isScore: true },
  { value: "financial_health", label: "Фін. стійкість", isScore: true },
  { value: "dynamics", label: "Динаміка", isScore: true },
];

export function MapPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [colorMode, setColorMode] = useState<ColorMode>("composite");
  const [viewMode, setViewMode] = useState<ViewMode>("facilities");
  const [activeNetwork, setActiveNetwork] = useState<{ edrpou: string; name: string; count: number } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["facilities-geojson", filterStatus],
    queryFn: () => {
      const p = new URLSearchParams();
      if (filterStatus) p.set("status", filterStatus);
      return api.get<FacilitiesGeoJSON>(`/api/v1/facilities.geojson?${p}`);
    },
    staleTime: 60_000,
  });

  // Lazy-load усіх підрозділів для режиму "all_points"
  const { data: allDivisions } = useQuery({
    queryKey: ["all-divisions-geojson"],
    queryFn: () => api.get<any>("/api/v1/divisions.geojson"),
    enabled: viewMode === "all_points",
    staleTime: 5 * 60_000,
  });

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    mapRef.current = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE,
      center: [31.0, 49.0],
      zoom: 5.4,
    });
    mapRef.current.addControl(new maplibregl.NavigationControl(), "top-right");
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // Push data → map source + setup layers
  useEffect(() => {
    if (!data || !mapRef.current) return;
    const m = mapRef.current;
    const setData = () => {
      const src = m.getSource("facilities") as maplibregl.GeoJSONSource | undefined;
      if (src) {
        src.setData(data as any);
        return;
      }
      m.addSource("facilities", { type: "geojson", data: data as any, cluster: true, clusterRadius: 50 });
      m.addLayer({
        id: "clusters",
        type: "circle",
        source: "facilities",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#2c5282",
          "circle-radius": ["step", ["get", "point_count"], 14, 50, 18, 200, 24],
          "circle-opacity": 0.8,
        },
      });
      m.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "facilities",
        filter: ["has", "point_count"],
        layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": 11 },
        paint: { "text-color": "#fff" },
      });
      m.addLayer({
        id: "facility-points",
        type: "circle",
        source: "facilities",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": 7,                  // лікарня крупна
          "circle-color": "#a0aec0",           // буде перевизначено через setPaintProperty
          "circle-stroke-width": 2,
          "circle-stroke-color": "#fff",
        },
      });
      m.on("click", "facility-points", (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const [lng, lat] = (feat.geometry as any).coordinates;
        const p = feat.properties as any;
        const scoreRow = (label: string, val: any, color = "#4a5568") =>
          val != null
            ? `<div style="font-size:11px"><span style="color:#a0aec0">${label}:</span> <strong style="color:${color}">${val}</strong></div>`
            : "";
        const html = `
          <div style="font-size:13px;line-height:1.4;max-width:280px">
            <div style="font-weight:600;color:#1a365d;margin-bottom:4px">${escape(p.name)}</div>
            <div style="color:#718096;font-size:11px">ЄДРПОУ ${p.edrpou}</div>
            <div style="color:#718096;font-size:11px">${p.area || "—"} · ${p.settlement_name || ""}</div>
            <div style="color:#718096;font-size:11px;margin-bottom:6px">${p.care_type || "—"} · ${p.status}</div>
            ${scoreRow("Composite", p.composite, scoreColor(p.composite))}
            ${scoreRow("Активність", p.activity)}
            ${scoreRow("Ефективність", p.efficiency)}
            ${scoreRow("Маршрутизація", p.care_routing)}
            ${scoreRow("Фін. стійкість", p.financial_health)}
            ${scoreRow("Динаміка", p.dynamics)}
            <a href="/facilities/${p.edrpou}" style="display:inline-block;margin-top:8px;padding:4px 10px;background:#2c5282;color:white;border-radius:3px;text-decoration:none;font-size:11px">Профіль →</a>
          </div>`;
        new Popup({ closeButton: true, maxWidth: "300px" })
          .setLngLat([lng, lat]).setHTML(html).addTo(m);
        // Автоматично показуємо мережу підрозділів
        loadNetwork(m, p.edrpou);
      });
      m.on("mouseenter", "facility-points", () => (m.getCanvas().style.cursor = "pointer"));
      m.on("mouseleave", "facility-points", () => (m.getCanvas().style.cursor = ""));
    };
    if (m.isStyleLoaded()) setData();
    else m.once("load", setData);
  }, [data]);

  // Listen for network load event (з popup-кнопки)
  useEffect(() => {
    const handler = () => {
      const info = (window as any).__hrsNetworkInfo;
      if (info) setActiveNetwork(info);
    };
    window.addEventListener("hrs-network-loaded", handler);
    return () => window.removeEventListener("hrs-network-loaded", handler);
  }, []);

  // Update colors коли змінюється colorMode (для facility-points і all-points-circle)
  useEffect(() => {
    if (!mapRef.current || !data) return;
    const m = mapRef.current;
    const apply = () => {
      const colorExpr = buildColorExpr(colorMode);
      if (m.getLayer("facility-points")) m.setPaintProperty("facility-points", "circle-color", colorExpr);
      if (m.getLayer("all-points-circle")) m.setPaintProperty("all-points-circle", "circle-color", colorExpr);
    };
    if (m.isStyleLoaded()) apply();
    else m.once("load", apply);
  }, [colorMode, data]);

  // ViewMode: показ/приховання шарів — чітко toggle ВСІ шари за обраним режимом
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const setVis = (id: string, vis: "visible" | "none") => {
        if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", vis);
      };

      // Шари юр.осіб (лікарень)
      const facilityLayers = ["facility-points", "clusters", "cluster-count"];
      // Шари підрозділів
      const divisionLayers = ["all-points-circle", "all-divisions-clusters", "all-divisions-cluster-count"];

      if (viewMode === "facilities") {
        facilityLayers.forEach((id) => setVis(id, "visible"));
        divisionLayers.forEach((id) => setVis(id, "none"));
        if (m.getLayer("facility-points")) m.setFilter("facility-points", ["!", ["has", "point_count"]]);
      } else if (viewMode === "network_only") {
        // показуємо тільки опорні юр.особи; кластери приховуємо
        setVis("facility-points", "visible");
        setVis("clusters", "none");
        setVis("cluster-count", "none");
        divisionLayers.forEach((id) => setVis(id, "none"));
        if (m.getLayer("facility-points")) {
          m.setFilter("facility-points", [
            "all",
            ["!", ["has", "point_count"]],
            ["any",
              ["==", ["get", "network_role"], "Надкластерний"],
              ["==", ["get", "network_role"], "Кластерний"],
              ["==", ["get", "network_role"], "Загальний"],
            ],
          ]);
        }
      } else if (viewMode === "all_points") {
        facilityLayers.forEach((id) => setVis(id, "none"));
        divisionLayers.forEach((id) => setVis(id, "visible"));
      }
    };
    if (m.isStyleLoaded()) apply();
    else m.once("load", apply);
  }, [viewMode]);

  // Завантажуємо all-divisions у map source
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !allDivisions) return;
    const apply = () => {
      const src = m.getSource("all-divisions") as maplibregl.GeoJSONSource | undefined;
      if (src) {
        src.setData(allDivisions);
      } else {
        m.addSource("all-divisions", { type: "geojson", data: allDivisions, cluster: true, clusterRadius: 40 });
        m.addLayer({
          id: "all-divisions-clusters",
          type: "circle",
          source: "all-divisions",
          filter: ["has", "point_count"],
          paint: {
            "circle-color": "#dd6b20",
            "circle-radius": ["step", ["get", "point_count"], 12, 50, 16, 200, 22],
            "circle-opacity": 0.7,
          },
        });
        m.addLayer({
          id: "all-divisions-cluster-count",
          type: "symbol",
          source: "all-divisions",
          filter: ["has", "point_count"],
          layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": 10 },
          paint: { "text-color": "#fff" },
        });
        m.addLayer({
          id: "all-points-circle",
          type: "circle",
          source: "all-divisions",
          filter: ["!", ["has", "point_count"]],
          paint: {
            "circle-radius": 4,                // підрозділ менший
            "circle-color": buildColorExpr(colorMode),
            "circle-stroke-width": 1,
            "circle-stroke-color": "#fff",
          },
        });
        m.on("click", "all-points-circle", (e) => {
          const f = e.features?.[0];
          if (!f) return;
          const [lng, lat] = (f.geometry as any).coordinates;
          const p = f.properties as any;
          const html = `
            <div style="font-size:12px;line-height:1.4;max-width:300px">
              <div style="font-weight:600;color:#dd6b20">${escape(p.name)}</div>
              <div style="color:#718096;font-size:10px">${p.type}</div>
              ${p.address ? `<div style="color:#4a5568;font-size:11px;margin-top:4px">📍 ${escape(p.address)}</div>` : ""}
              <div style="margin-top:8px;padding:8px;background:#fffaf0;border:1px solid #fbd38d;border-radius:4px">
                <div style="font-size:10px;color:#7b341e;font-weight:600;margin-bottom:2px">⚠ Це не самостійна лікарня</div>
                <div style="font-size:10px;color:#718096">Контракт з НСЗУ і рейтинг — у юр.особи:</div>
                <a href="/facilities/${p.parent_edrpou}" style="font-size:11px;color:#2c5282;font-weight:600;text-decoration:none">${escape(p.parent_name)}</a>
                ${p.composite != null ? `<div style="font-size:10px;color:#a0aec0;margin-top:2px">Composite: <strong style="color:${scoreColor(p.composite)}">${p.composite}</strong></div>` : ""}
              </div>
            </div>`;
          new Popup({ closeButton: true, maxWidth: "300px" })
            .setLngLat([lng, lat]).setHTML(html).addTo(m);
        });
        m.on("mouseenter", "all-points-circle", () => (m.getCanvas().style.cursor = "pointer"));
        m.on("mouseleave", "all-points-circle", () => (m.getCanvas().style.cursor = ""));
      }
      // visibility based on viewMode
      const vis = viewMode === "all_points" ? "visible" : "none";
      ["all-divisions-clusters", "all-divisions-cluster-count", "all-points-circle"].forEach((id) => {
        if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", vis);
      });
    };
    if (m.isStyleLoaded()) apply();
    else m.once("load", apply);
  }, [allDivisions, viewMode]);

  const counts = useMemo(() => {
    if (!data) return null;
    const by: Record<string, number> = {};
    for (const f of data.features) {
      const s = f.properties.status;
      by[s] = (by[s] || 0) + 1;
    }
    return by;
  }, [data]);

  const scoreStats = useMemo(() => {
    if (!data || colorMode === "status") return null;
    const vals = data.features
      .map((f) => (f.properties as any)[colorMode] as number | null)
      .filter((v): v is number => v !== null && v !== undefined);
    if (vals.length === 0) return null;
    return {
      count: vals.length,
      missing: data.features.length - vals.length,
      avg: vals.reduce((a, b) => a + b, 0) / vals.length,
    };
  }, [data, colorMode]);

  const currentMode = COLOR_MODES.find((m) => m.value === colorMode)!;

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <aside style={{ width: 280, padding: 16, background: "white", borderRight: "1px solid #e2e8f0", overflow: "auto" }}>
        <h2 style={{ margin: "0 0 12px", color: "#1a365d", fontSize: 16 }}>Карта лікарень</h2>
        <div style={{ fontSize: 12.5, color: "#718096", marginBottom: 12 }}>
          {isLoading && "Завантаження…"}
          {data && <>Усього: <strong>{data._count}</strong> з координатами</>}
          {error && <span style={{ color: "#c53030" }}>Помилка: {(error as any)?.detail}</span>}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, color: "#4a5568", marginBottom: 4, fontWeight: 600 }}>
            Що показувати:
          </label>
          <select value={viewMode} onChange={(e) => setViewMode(e.target.value as ViewMode)}
                  style={selectStyle}>
            <option value="facilities">🏥 Лікарні (юр.особи) — {data?._count || "…"}</option>
            <option value="network_only">🌐 Тільки опорна мережа (надкластерні + кластерні + загальні)</option>
            <option value="all_points">📍 Усі точки надання послуг (~11K)</option>
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, color: "#4a5568", marginBottom: 4, fontWeight: 600 }}>
            Розфарбувати за:
          </label>
          <select value={colorMode} onChange={(e) => setColorMode(e.target.value as ColorMode)}
                  style={selectStyle}>
            {COLOR_MODES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, color: "#4a5568", marginBottom: 4 }}>Фільтр статусу</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">всі</option>
            <option value="active">🟢 Діюча</option>
            <option value="new">🆕 Нова</option>
            <option value="war_zone">🔴 Зона бойових дій</option>
            <option value="relocated">🟠 Переміщена</option>
          </select>
        </div>

        {/* Активна мережа підрозділів */}
        {activeNetwork && (
          <div style={{
            marginBottom: 12, padding: 10, background: "#fffaf0",
            border: "1px solid #fbd38d", borderRadius: 4,
          }}>
            <div style={{ fontSize: 11, color: "#7b341e", fontWeight: 600, marginBottom: 4 }}>
              🌐 Активна мережа
            </div>
            <div style={{ fontSize: 12, color: "#2d3748", marginBottom: 4 }}>
              {activeNetwork.name.length > 50 ? activeNetwork.name.slice(0, 50) + "…" : activeNetwork.name}
            </div>
            <div style={{ fontSize: 11, color: "#718096", marginBottom: 6 }}>
              {activeNetwork.count} підрозділ{activeNetwork.count === 1 ? "" : "ів"}
            </div>
            <button
              onClick={() => {
                if (mapRef.current) clearNetwork(mapRef.current);
                setActiveNetwork(null);
              }}
              style={{
                padding: "4px 10px", fontSize: 11, background: "white",
                border: "1px solid #cbd5e0", borderRadius: 3, cursor: "pointer",
                color: "#4a5568",
              }}
            >
              Прибрати мережу
            </button>
          </div>
        )}

        {/* Легенда */}
        <div style={{ marginTop: 16, padding: 10, background: "#f7fafc", borderRadius: 4 }}>
          <div style={{ fontSize: 12, color: "#2c5282", fontWeight: 600, marginBottom: 6 }}>
            Легенда: {currentMode.label}
          </div>
          {currentMode.isScore ? (
            <ScoreLegend />
          ) : (
            counts && Object.entries(counts).map(([st, n]) => (
              <div key={st} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "2px 0" }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: STATUS_COLORS[st] || "#a0aec0" }} />
                <span style={{ flex: 1 }}>{st}</span>
                <strong>{n}</strong>
              </div>
            ))
          )}
        </div>

        {scoreStats && (
          <div style={{ marginTop: 12, padding: 10, background: "#ebf8ff", borderRadius: 4, fontSize: 12 }}>
            <div style={{ color: "#2c5282", fontWeight: 600, marginBottom: 4 }}>Статистика по {currentMode.label}:</div>
            <div>Лікарень з даними: <strong>{scoreStats.count}</strong></div>
            <div>Без даних: <strong>{scoreStats.missing}</strong></div>
            <div>Середнє: <strong>{scoreStats.avg.toFixed(1)}</strong></div>
          </div>
        )}

        <div style={{ marginTop: 24, fontSize: 11.5, color: "#a0aec0", lineHeight: 1.5 }}>
          Клік на маркер → popup з усіма scores + посилання на профіль.
          Сірі точки — у яких немає score (data_sufficient=false або індекс не розрахований).
        </div>
      </aside>
      <div ref={containerRef} style={{ flex: 1, position: "relative" }} />
    </div>
  );
}

// Завантажує network з backend і малює layers: division-points + division-links.
async function loadNetwork(
  m: MLMap,
  edrpou: string,
  onLoaded?: (info: { edrpou: string; name: string; count: number }) => void,
) {
  const data = await api.get<any>(`/api/v1/facilities/${edrpou}/divisions.geojson`);
  const divisions = {
    type: "FeatureCollection",
    features: data.features.filter((f: any) => f.properties.kind === "division"),
  };
  const links = {
    type: "FeatureCollection",
    features: data.features.filter((f: any) => f.properties.kind === "link"),
  };
  const main = {
    type: "FeatureCollection",
    features: data.features.filter((f: any) => f.properties.kind === "main"),
  };

  const upsertSrc = (id: string, data: any) => {
    const src = m.getSource(id) as maplibregl.GeoJSONSource | undefined;
    if (src) src.setData(data);
    else m.addSource(id, { type: "geojson", data });
  };
  upsertSrc("division-links", links);
  upsertSrc("division-points", divisions);
  upsertSrc("network-main", main);

  if (!m.getLayer("division-links-line")) {
    m.addLayer({
      id: "division-links-line",
      type: "line",
      source: "division-links",
      paint: {
        "line-color": "#dd6b20",
        "line-width": 1.5,
        "line-opacity": 0.55,
        "line-dasharray": [2, 1],
      },
    }, "facility-points");
  }
  if (!m.getLayer("network-main-circle")) {
    // Синя крупна точка — головний заклад мережі (поверх інших шарів)
    m.addLayer({
      id: "network-main-circle",
      type: "circle",
      source: "network-main",
      paint: {
        "circle-radius": 9,
        "circle-color": "#2c5282",
        "circle-stroke-color": "#fff",
        "circle-stroke-width": 2,
      },
    });
  }
  if (!m.getLayer("division-points-circle")) {
    m.addLayer({
      id: "division-points-circle",
      type: "circle",
      source: "division-points",
      paint: {
        "circle-radius": 4,
        "circle-color": "#dd6b20",
        "circle-stroke-color": "#fff",
        "circle-stroke-width": 1,
      },
    });
    m.on("click", "division-points-circle", (e) => {
      const f = e.features?.[0];
      if (!f) return;
      const [lng, lat] = (f.geometry as any).coordinates;
      const p = f.properties as any;
      const parentBlock = p.parent_edrpou
        ? `<div style="margin-top:8px;padding:8px;background:#fffaf0;border:1px solid #fbd38d;border-radius:4px">
             <div style="font-size:10px;color:#7b341e;font-weight:600;margin-bottom:2px">⚠ Це не самостійна лікарня</div>
             <div style="font-size:10px;color:#718096">Контракт з НСЗУ і рейтинг — у юр.особи:</div>
             <a href="/facilities/${p.parent_edrpou}" style="font-size:11px;color:#2c5282;font-weight:600;text-decoration:none">${escape(p.parent_name || "—")}</a>
             <div style="font-size:10px;color:#a0aec0;margin-top:2px">ЄДРПОУ ${p.parent_edrpou} · мережа з ${p.siblings_count || "?"} підрозділів</div>
           </div>`
        : "";
      new Popup({ closeButton: true, maxWidth: "300px" })
        .setLngLat([lng, lat])
        .setHTML(`
          <div style="font-size:12px;line-height:1.4;max-width:280px">
            <div style="font-weight:600;color:#1a365d">${escape(p.name)}</div>
            <div style="color:#718096;font-size:11px">${p.type}</div>
            ${p.address ? `<div style="color:#4a5568;font-size:11px;margin-top:4px">📍 ${escape(p.address)}</div>` : ""}
            ${p.phone ? `<div style="color:#4a5568;font-size:11px">☎ ${p.phone}</div>` : ""}
            ${p.email ? `<div style="color:#4a5568;font-size:11px">✉ ${p.email}</div>` : ""}
            ${parentBlock}
          </div>`)
        .addTo(m);
    });
    m.on("mouseenter", "division-points-circle", () => (m.getCanvas().style.cursor = "pointer"));
    m.on("mouseleave", "division-points-circle", () => (m.getCanvas().style.cursor = ""));
  }

  // Fit bounds: main + всі divisions
  const coords = data.features
    .filter((f: any) => f.geometry.type === "Point")
    .map((f: any) => f.geometry.coordinates);
  if (coords.length > 1) {
    const lngs = coords.map((c: any) => c[0]);
    const lats = coords.map((c: any) => c[1]);
    m.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 60, maxZoom: 13, duration: 800 },
    );
  }

  if (onLoaded) onLoaded({ edrpou, name: data.name, count: data._count_divisions });
  // Зберігаємо в window для доступу з button-handler-а через onLoaded callback
  (window as any).__hrsNetworkInfo = { edrpou, name: data.name, count: data._count_divisions };
  window.dispatchEvent(new CustomEvent("hrs-network-loaded"));
}

function clearNetwork(m: MLMap) {
  ["network-main-circle", "division-points-circle", "division-links-line"].forEach((id) => {
    if (m.getLayer(id)) m.removeLayer(id);
  });
  ["network-main", "division-points", "division-links"].forEach((id) => {
    if (m.getSource(id)) m.removeSource(id);
  });
}

// Будує MapLibre expression для кольору точки на основі colorMode.
function buildColorExpr(mode: ColorMode): any {
  if (mode === "status") {
    return [
      "match",
      ["get", "status"],
      "active", STATUS_COLORS.active,
      "new", STATUS_COLORS.new,
      "war_zone", STATUS_COLORS.war_zone,
      "relocated", STATUS_COLORS.relocated,
      "closed", STATUS_COLORS.closed,
      "#a0aec0",
    ];
  }
  // Score: interpolate gradient. Якщо null → сірий
  return [
    "case",
    ["==", ["get", mode], null],
    "#cbd5e0",  // немає даних
    [
      "interpolate",
      ["linear"],
      ["get", mode],
      ...SCORE_GRADIENT.flatMap((g) => [g.stop, g.color]),
    ],
  ];
}

function scoreColor(v: number | null | undefined): string {
  if (v == null) return "#a0aec0";
  if (v >= 75) return "#22543d";
  if (v >= 50) return "#48bb78";
  if (v >= 25) return "#dd6b20";
  return "#c53030";
}

function ScoreLegend() {
  return (
    <div>
      <div style={{
        height: 12, borderRadius: 6,
        background: `linear-gradient(to right, ${SCORE_GRADIENT.map((g) => `${g.color} ${g.stop}%`).join(", ")})`,
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#718096", marginTop: 2 }}>
        <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 11, color: "#718096" }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#cbd5e0" }} />
        <span>немає даних</span>
      </div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  width: "100%", padding: "6px 8px", border: "1px solid #cbd5e0",
  borderRadius: 4, fontSize: 13,
};

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c)
  );
}
